type PreviewVideoInput = {
  source?: "upload" | "url" | string | null;
  url?: string | null;
  file?: {
    asset?: {
      url?: string | null;
    };
  } | null;
};

export type ResolvedCoursePreviewVideo = {
  src: string;
  embedUrl: string | null;
  thumbnailSrc: string | null;
};

function trimString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getYouTubeVideoId(rawUrl: string): string | null {
  try {
    const parsed = new URL(rawUrl);
    const host = parsed.hostname.replace(/^www\./, "");

    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = parsed.searchParams.get("v");
      return trimString(id);
    }

    if (host === "youtu.be") {
      return trimString(parsed.pathname.replace("/", ""));
    }
  } catch {
    return null;
  }

  return null;
}

function getVimeoVideoId(rawUrl: string): string | null {
  try {
    const parsed = new URL(rawUrl);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "vimeo.com") {
      return trimString(parsed.pathname.replace("/", ""));
    }
    if (host === "player.vimeo.com") {
      const segments = parsed.pathname.split("/").filter(Boolean);
      const id = segments.length > 0 ? segments[segments.length - 1] : null;
      return trimString(id);
    }
  } catch {
    return null;
  }
  return null;
}

function toEmbedUrl(rawUrl: string): string | null {
  const youtubeId = getYouTubeVideoId(rawUrl);
  if (youtubeId) return `https://www.youtube.com/embed/${youtubeId}`;

  const vimeoId = getVimeoVideoId(rawUrl);
  if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}`;

  return null;
}

function toThumbnailUrl(rawUrl: string): string | null {
  const youtubeId = getYouTubeVideoId(rawUrl);
  if (youtubeId) {
    return `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;
  }

  return null;
}

export function appendVideoStartFragment(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) return trimmed;
  if (trimmed.includes("#t=")) return trimmed;
  return `${trimmed}#t=0.001`;
}

export function resolveCoursePreviewVideo(input: PreviewVideoInput | null | undefined): ResolvedCoursePreviewVideo | null {
  if (!input) return null;

  const bySource =
    input.source === "upload"
      ? trimString(input.file?.asset?.url)
      : input.source === "url"
      ? trimString(input.url)
      : null;

  const fallback = trimString(input.url) ?? trimString(input.file?.asset?.url);
  const src = bySource ?? fallback;

  if (!src) return null;

  return {
    src,
    embedUrl: toEmbedUrl(src),
    thumbnailSrc: toThumbnailUrl(src),
  };
}
